import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup, Checkbox } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface ExportItem {
  id: string;
  rank: string;
  title: string;
  outcome: string;
  effort: string;
  impact: string;
  confidence: string;
  risk: string;
  timeWindow: string;
  dibb: string;
  owner: string;
}

export default function BacklogExportPage() {
  const [items, setItems] = useState<ExportItem[]>([
    { id: '1', rank: '', title: '', outcome: '', effort: '', impact: '', confidence: '', risk: '', timeWindow: '', dibb: '', owner: '' },
  ]);
  const [hasQuickWins, setHasQuickWins] = useState(false);
  const [hasBoldBets, setHasBoldBets] = useState(false);

  const updateItem = (id: string, field: keyof ExportItem, value: string) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    if (items.length < 20) {
      setItems([...items, { id: Date.now().toString(), rank: '', title: '', outcome: '', effort: '', impact: '', confidence: '', risk: '', timeWindow: '', dibb: '', owner: '' }]);
    }
  };

  return (
    <TaskPage
      phase="gather"
      title="Backlog Export"
      pageNumber={7}
      instruction="Filters to apply (in order): 1. Time-window: now → next → later. 2. Within now/next, sort by Impact then Confidence. 3. Keep the top ~20 items; move the rest to 'Later.'"
      whyItMatters="Making the shortlist explicit (and limited) reduces future thrash and makes later comparison faster; keep the table lean and scannable."
      coachRail={{
        timing: "10-15 minutes",
        tips: "Quality check: do we have at least 2-3 quick wins and 1-2 bold bets?",
        debrief: "Is our backlog ready for Arrange? What's missing?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 mb-4">
          <p className="text-sm text-[var(--text-sec)]">
            <strong>Keep the top ~20 items.</strong> Move the rest to "Later."
          </p>
        </div>

        <div className="bg-[var(--surface)] p-6 overflow-x-auto">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Export Sheet</h3>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Rank</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Title</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Outcome</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Effort</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Impact</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Confidence</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Risk</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Time-window</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">D/I/B/B</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Owner</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-[var(--rule)]">
                  <td className="p-2">
                    <TextInput
                      value={item.rank}
                      onChange={(e) => updateItem(item.id, 'rank', e.target.value)}
                      placeholder="#"
                      className="text-xs w-12"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.title}
                      onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                      placeholder="Title"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.outcome}
                      onChange={(e) => updateItem(item.id, 'outcome', e.target.value)}
                      placeholder="Outcome"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'S', label: 'S' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.effort}
                      onChange={(value) => updateItem(item.id, 'effort', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'S', label: 'S' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.impact}
                      onChange={(value) => updateItem(item.id, 'impact', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'H', label: 'H' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.confidence}
                      onChange={(value) => updateItem(item.id, 'confidence', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'H', label: 'H' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.risk}
                      onChange={(value) => updateItem(item.id, 'risk', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'now', label: 'Now' },
                        { value: 'next', label: 'Next' },
                        { value: 'later', label: 'Later' }
                      ]}
                      value={item.timeWindow}
                      onChange={(value) => updateItem(item.id, 'timeWindow', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'D', label: 'D' },
                        { value: 'I', label: 'I' },
                        { value: 'B', label: 'B' }
                      ]}
                      value={item.dibb}
                      onChange={(value) => updateItem(item.id, 'dibb', value)}
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.owner}
                      onChange={(e) => updateItem(item.id, 'owner', e.target.value)}
                      placeholder="Owner"
                      className="text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length < 20 && (
            <button
              onClick={addItem}
              className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
            >
              + Add another item (max 20)
            </button>
          )}
        </div>

        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6">
          <h3 className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-4">Quality Check</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={hasQuickWins}
                onChange={(e) => setHasQuickWins(e.target.checked)}
                label="We have at least 2-3 quick wins"
              />
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={hasBoldBets}
                onChange={(e) => setHasBoldBets(e.target.checked)}
                label="We have at least 1-2 bold bets"
              />
            </label>
          </div>
        </div>

        <Callout type="tip" phase="gather">
          <p className="text-sm">A balanced backlog has quick wins (fast, high confidence) and bold bets (bigger impact, higher risk).</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






